import { Component, inject, signal, computed, effect, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { httpsCallable } from 'firebase/functions';
import {
  collection, deleteDoc, doc, onSnapshot, Unsubscribe,
} from 'firebase/firestore';
import {
  CrwdButton, CrwdChat, CrwdChatInput, CrwdChatMessage, CrwdEmptyState, CrwdHelpPanel,
} from '@crewdle/ui-kit';
import { CrwdMarkdown } from '@crewdle/ui-kit/markdown';
import { FirebaseService } from '../core/services/firebase.service';
import { DataStoreService } from '../core/services/data-store.service';
import { OrgContextService } from '../core/services/org-context.service';
import { SupportChatbotService } from './support-chatbot.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * The support agent panel. Chrome, transcript, bubbles and composer are all kit
 * components now (crwd-help-panel / crwd-chat / crwd-chat-message /
 * crwd-chat-input) — crwd-help-panel was extracted from this component's own
 * dimensions, so the shape is unchanged. What stays app-side is the streaming
 * transport, the run cancellation, and the pendingActions channel that lets the
 * agent drive the router.
 */
@Component({
  selector: 'app-support-chatbot',
  standalone: true,
  imports: [
    TranslocoModule, CrwdButton, CrwdChat, CrwdChatInput, CrwdChatMessage,
    CrwdEmptyState, CrwdHelpPanel, CrwdMarkdown,
  ],
  styles: [`
    :host {
      display: contents;
    }
    /* All that is left of this component's own styling: the no-credits CTA and
       the error line. The message scale, markdown rhythm and code theme ship
       inside the kit. */
    .sc-error {
      margin-top: 0.5rem;
      color: var(--color-error-500, #ff5322);
      font-size: 0.8125rem;
    }
  `],
  template: `
    <ng-container *transloco="let t">
      @if (authed()) {
        <!-- Deferred on first open: the kit markdown renderer pulls in marked
             and highlight.js, and this component is mounted eagerly at the app
             root. Every kit part below is used ONLY inside this block, so they
             all land in the lazy chunk rather than the initial bundle. -->
        @defer (when open()) {
          <crwd-help-panel
            [(open)]="open"
            [heading]="t('support-chatbot.title')"
            [closeAriaLabel]="t('support-chatbot.close')"
          >
            <crwd-chat width="full" bareTranscript>
              @if (messages().length === 0 && hasOrg()) {
                @if (hasCredits()) {
                  <crwd-empty-state [description]="t('support-chatbot.empty-state')" />
                } @else {
                  <!-- crwd-empty-state projects ONLY into its named slots; a
                       child without the attribute is silently dropped. -->
                  <crwd-empty-state [description]="t('support-chatbot.no-credits')">
                    <crwd-button crwd-empty-state-actions size="sm" (pressed)="goToBilling()">
                      {{ t('support-chatbot.buy-credits') }}
                    </crwd-button>
                  </crwd-empty-state>
                }
              }

              @for (msg of messages(); track $index) {
                <crwd-chat-message
                  [sender]="msg.role"
                  [pending]="!msg.content && sending() && msg.role === 'assistant'"
                >
                  @if (msg.role === 'user') {
                    {{ msg.content }}
                  } @else if (msg.content) {
                    <!-- streaming keeps a half-arrived fence escaped instead
                         of highlighting a broken code block on every token. -->
                    <crwd-markdown [content]="msg.content" [streaming]="$last && sending()" breaks />
                  }
                </crwd-chat-message>
              }

              @if (errorMsg()) {
                <p class="sc-error">{{ errorMsg() }}</p>
              }

              <!-- The composer flips send to stop on its own while streaming,
                   which is why the panel header carries no Stop button. -->
              <crwd-chat-input
                chat-composer
                [(value)]="input"
                [placeholder]="t('support-chatbot.placeholder')"
                [rows]="1"
                [disabled]="!hasCredits()"
                [streaming]="sending()"
                [sendAriaLabel]="t('support-chatbot.send')"
                [stopAriaLabel]="t('support-chatbot.stop')"
                (send)="send()"
                (stop)="cancel()"
              />
            </crwd-chat>
          </crwd-help-panel>
        }
      }
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SupportChatbotComponent {
  private firebase = inject(FirebaseService);
  private dataStore = inject(DataStoreService);
  private orgContext = inject(OrgContextService);
  private supportChatbot = inject(SupportChatbotService);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  /** The service's own signal — crwd-help-panel's two-way `open` writes straight through. */
  readonly open = this.supportChatbot.open;
  readonly messages = signal<ChatMessage[]>([]);
  readonly sending = signal(false);
  readonly errorMsg = signal('');
  readonly authed = computed(() => !!this.dataStore.authUser());
  readonly hasOrg = computed(() => !!this.orgContext.currentOrg());
  readonly hasCredits = computed(() => {
    const org = this.orgContext.currentOrg();
    return !!org?.overageAllowed || (org?.creditBalance ?? 0) > 0;
  });

  readonly input = signal('');

  private sessionId: string | null = null;
  private currentRunId: string | null = null;
  private abortController: AbortController | null = null;
  private pendingActionsUnsub: Unsubscribe | null = null;
  private readonly dispatchedActionIds = new Set<string>();
  private lastAuthUid: string | undefined = this.dataStore.authUser()?.uid ?? undefined;

  constructor() {
    // New sessionId every time the panel opens; drop transcript when closed.
    // The pendingActions subscription is attached lazily on 'run-started',
    // since its Firestore rule requires the parent session doc (written
    // server-side on the first request) to exist first.
    effect(() => {
      if (this.open()) {
        if (!this.sessionId) this.sessionId = this.generateSessionId();
      } else {
        this.resetSession();
      }
    });

    // When the signed-in user changes identity (logout, account switch) the
    // previous transcript must not leak into the next session. Close the
    // panel and wipe local state.
    effect(() => {
      const uid = this.dataStore.authUser()?.uid ?? undefined;
      if (uid !== this.lastAuthUid) {
        this.lastAuthUid = uid;
        if (this.supportChatbot.open()) this.supportChatbot.open.set(false);
        this.resetSession();
      }
    });
  }

  goToBilling() {
    this.supportChatbot.open.set(false);
    void this.router.navigateByUrl(this.orgContext.orgUrl('/billing'));
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetSession() {
    this.abortController?.abort();
    this.abortController = null;
    this.pendingActionsUnsub?.();
    this.pendingActionsUnsub = null;
    this.dispatchedActionIds.clear();
    this.sessionId = null;
    this.currentRunId = null;
    this.messages.set([]);
    this.errorMsg.set('');
    this.sending.set(false);
  }

  /**
   * Listen for server-side tool outputs that need to drive the UI
   * (navigation with optional intent, etc.). Each action doc is dispatched
   * once and deleted.
   */
  private subscribePendingActions(sessionId: string) {
    if (this.pendingActionsUnsub) return;
    const ref = collection(this.firebase.firestore, `supportSessions/${sessionId}/pendingActions`);
    this.pendingActionsUnsub = onSnapshot(
      ref,
      (snap) => {
        for (const change of snap.docChanges()) {
          if (change.type !== 'added') continue;
          const docId = change.doc.id;
          if (this.dispatchedActionIds.has(docId)) continue;
          this.dispatchedActionIds.add(docId);
          const data = change.doc.data() as { action?: string; params?: Record<string, unknown> };
          void this.runPendingAction(sessionId, docId, data.action, data.params ?? {});
        }
      },
      (err) => {
        console.warn('[support-chatbot] pendingActions subscription error:', err);
        this.errorMsg.set(this.transloco.translate('support-chatbot.generic-error'));
      },
    );
  }

  private async runPendingAction(
    sessionId: string,
    docId: string,
    action: string | undefined,
    params: Record<string, unknown>,
  ): Promise<void> {
    try {
      await this.dispatchPendingAction(action, params);
    } catch (err) {
      console.warn('[support-chatbot] failed to dispatch pending action:', action, err);
      this.errorMsg.set(this.transloco.translate('support-chatbot.generic-error'));
      // Leave the allow-list entry in place so we don't retry a known-bad
      // action on every reconnect, but delete the doc so it doesn't pile up.
    }
    try {
      await deleteDoc(doc(this.firebase.firestore,
        `supportSessions/${sessionId}/pendingActions/${docId}`));
    } catch (err) {
      console.warn('[support-chatbot] failed to delete dispatched action doc:', err);
    }
  }

  private async dispatchPendingAction(action: string | undefined, params: Record<string, unknown>): Promise<void> {
    if (action === 'navigate') {
      const route = typeof params['route'] === 'string' ? (params['route'] as string) : null;
      const intent = typeof params['intent'] === 'string' ? (params['intent'] as string) : undefined;
      if (!route) throw new Error('navigate action missing route');
      const extras = intent ? { queryParams: { action: intent } } : undefined;
      // Agent routes are org-less (`/billing`): keep them inside this tab's org URL.
      const ok = await this.router.navigateByUrl(
        this.orgContext.orgUrl(this.router.createUrlTree([route], extras).toString()),
      );
      // If the router refuses (typically because a route guard redirected the
      // user elsewhere — e.g. they lack the page's read-permission), treat it
      // as an expected outcome rather than a crash. The agent is taught to
      // refuse these upfront; if one slips through, the guard's redirect is
      // the product's correct behavior, and surfacing "Something went wrong"
      // on top of it scares the user for no reason.
      if (!ok) {
        console.warn('[support-chatbot] router refused navigation to', route, '— guard likely redirected.');
      }
      return;
    }
    throw new Error(`unknown pending action: ${action}`);
  }

  async cancel() {
    if (!this.sessionId || !this.currentRunId) {
      this.abortController?.abort();
      return;
    }
    try {
      const call = httpsCallable<
        { sessionId: string; runId: string },
        { cancelled: boolean; status: string }
      >(this.firebase.functions, 'cancelSupportRun');
      await call({ sessionId: this.sessionId, runId: this.currentRunId });
    } catch {
      // Best-effort — the abort controller also trips the local stream.
    }
    this.abortController?.abort();
  }

  async send() {
    const text = this.input().trim();
    if (!text || this.sending() || !this.hasCredits()) return;
    if (!this.sessionId) this.sessionId = this.generateSessionId();

    this.errorMsg.set('');
    this.messages.set([
      ...this.messages(),
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ]);
    this.input.set('');
    this.sending.set(true);

    try {
      await this.stream(text);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.errorMsg.set(err?.message || this.transloco.translate('support-chatbot.generic-error'));
      }
    } finally {
      this.sending.set(false);
      this.currentRunId = null;
      this.abortController = null;
    }
  }

  private async stream(message: string): Promise<void> {
    // The org's regional project only accepts its own ID tokens.
    const user = this.firebase.orgAuth.currentUser;
    if (!user) {
      this.errorMsg.set(this.transloco.translate('support-chatbot.not-authenticated'));
      return;
    }
    const idToken = await user.getIdToken();
    const endpoint = this.endpointUrl();

    this.abortController = new AbortController();
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message,
        app: 'admin',
        orgId: this.orgContext.currentOrg()?.id,
      }),
      signal: this.abortController.signal,
    });

    if (resp.status === 402) {
      throw new Error(this.transloco.translate('support-chatbot.no-credits'));
    }
    if (!resp.ok || !resp.body) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Server ${resp.status}: ${body || resp.statusText}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // Ignore malformed frame
          }
        }
      }
    }
  }

  private handleEvent(event: any): void {
    const type = event?.type as string | undefined;
    if (!type) return;

    if (type === 'run-started') {
      this.currentRunId = event.runId;
      if (this.sessionId) this.subscribePendingActions(this.sessionId);
      return;
    }

    if (type === 'token' && typeof event.token === 'string') {
      this.appendToLastAssistant(event.token);
      return;
    }

    if (type === 'error' && typeof event.error === 'string') {
      const code = event.error;
      const knownKey = `support-chatbot.${code}`;
      const translated = this.transloco.translate(knownKey);
      this.errorMsg.set(translated !== knownKey ? translated : code);
      return;
    }

    // 'done', 'generation-status', etc. — no UI change required.
  }

  private appendToLastAssistant(chunk: string) {
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'assistant') {
      this.messages.set([...msgs, { role: 'assistant', content: chunk }]);
      return;
    }
    const updated: ChatMessage = { role: 'assistant', content: last.content + chunk };
    this.messages.set([...msgs.slice(0, -1), updated]);
  }

  // The active org's regional project + functions region (data residency).
  private endpointUrl(): string {
    return this.firebase.functionUrl('runSupportAgent');
  }
}
